if (!customElements.get('closecarry-page')) {
  class CloseCarryPage extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.tabSelections = new Map();
      const options = { signal: this.controller.signal };
      ['scroll', 'resize'].forEach(name => {
        window.addEventListener(name, () => this.scheduleStickyUpdate(name === 'resize'), { ...options, passive: true });
      });
      this.addEventListener('click', event => {
        const link = event.target.closest('.cc-cart-link');
        const drawer = document.querySelector('cart-drawer');
        if (link && typeof drawer?.open === 'function') {
          event.preventDefault();
          this.openCartDrawer(drawer, link);
          return;
        }
        this.onClick(event);
      }, options);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshCartCount());
        this.controller.signal.addEventListener('abort', () => this.unsubscribeCart?.(), { once: true });
      }
      this.addEventListener('submit', event => {
        if (event.target.matches('.cc-form')) this.addToCart(event);
      }, options);
      this.addEventListener('change', event => {
        if (event.target.matches('.cc-form [name="id"]')) this.selectVariant(event.target.value, true);
        if (event.target.matches('.cc-form [name="quantity"]')) this.clampQuantity();
      }, options);
      this.addEventListener('input', event => {
        if (event.target.matches('[data-cc-waist]')) {
          delete event.target.dataset.centimeters;
          const result = event.target.closest('.cc-fit-check')?.querySelector('[data-cc-fit-result]');
          if (result) result.hidden = true;
        }
      }, options);
      this.addEventListener('keydown', event => this.onKeydown(event), options);
      this.addEventListener('load', event => {
        if (event.target.matches?.('.cc-gallery-slide img')) this.warmNext(event.target);
      }, { ...options, capture: true });
      ['pointerover', 'focusin'].forEach(name => this.addEventListener(name, event => {
        const thumbnail = event.target.closest?.('[data-cc-thumbnail]');
        if (thumbnail) this.warmSlide(Number(thumbnail.dataset.ccThumbnail));
      }, options));
      this.addEventListener('pointerdown', event => {
        if (!event.isPrimary || !event.target.closest('.cc-gallery-stage')) return;
        this.gesture = { x: event.clientX, y: event.clientY, id: event.pointerId };
      }, options);
      this.addEventListener('pointerup', event => {
        if (!this.gesture || this.gesture.id !== event.pointerId) return;
        const x = event.clientX - this.gesture.x;
        const y = event.clientY - this.gesture.y;
        this.gesture = null;
        if (Math.abs(x) > 45 && Math.abs(x) > Math.abs(y) * 1.3) {
          this.showSlide(this.currentSlide + (x < 0 ? 1 : -1));
          this.suppressZoomUntil = performance.now() + 350;
        }
      }, options);
      this.addEventListener('pointercancel', () => { this.gesture = null; }, options);
      this.addEventListener('close', event => {
        if (event.target.matches('[data-cc-lightbox]')) {
          this.cancelZoom();
          if (this.zoomOpener?.isConnected) this.zoomOpener.focus({ preventScroll: true });
        }
        if (event.target.matches('[data-cc-added]') && this.cartOpener?.isConnected) this.cartOpener.focus({ preventScroll: true });
      }, { ...options, capture: true });
      ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(name => {
        document.addEventListener(name, () => {
          cancelAnimationFrame(this.refreshFrame);
          this.refreshFrame = requestAnimationFrame(() => this.refresh());
        }, options);
      });
      document.addEventListener('shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        const slide = event.target.closest('[data-cc-slide]') || event.target.querySelector('[data-cc-slide]');
        if (slide) { this.editorGalleryBlock = slide.dataset.galleryBlock; this.showSlide(Number(slide.dataset.ccSlide), true); }
        const tab = event.target.closest('[data-cc-tab]') || event.target.querySelector('[data-cc-tab]');
        if (tab) this.activateTab(tab.closest('[data-cc-tabs]'), tab.dataset.ccTab, false, true);
        const swatch = event.target.closest('[data-cc-variant]');
        if (swatch) this.selectVariant(swatch.dataset.ccVariant, true, false);
        const disclosure = event.target.closest('details') || event.target.querySelector('details');
        if (disclosure) disclosure.open = true;
      }, options);
      window.addEventListener('popstate', () => {
        const id = new URL(location.href).searchParams.get('variant');
        if (id) this.selectVariant(id, true, false);
        else if (this.variants?.length) { this.selectVariant(this.variants[0].id, false, false); this.showSlide(0); }
      }, options);
      this.refresh();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.sectionImageObserver?.disconnect();
      cancelAnimationFrame(this.refreshFrame);
      cancelAnimationFrame(this.stickyFrame);
      this.stickyFrame = null;
      this.cancelZoom();
      this.cancelGalleryImage();
    }
    refresh() {
      this.cancelGalleryImage();
      const previousId = this.currentVariant?.id;
      const previousBlock = this.editorGalleryBlock || this.slides?.[this.currentSlide]?.dataset.galleryBlock;
      const product = this.querySelector('[data-cc-product]');
      const showVariant = previousId ? Boolean(this.showingVariant) : product?.dataset.initialVariant === 'true';
      this.gallerySizes = product?.dataset.gallerySizes || '100vw';
      this.form = this.querySelector('.cc-form');
      this.variantSelect = this.form?.querySelector('[name="id"]');
      this.quantity = this.form?.querySelector('[name="quantity"]');
      try { this.variants = JSON.parse(this.querySelector('[data-cc-variants]')?.textContent || '[]'); } catch (_) { this.variants = []; }
      try { this.words = JSON.parse(this.querySelector('[data-cc-words]')?.textContent || '{}'); } catch (_) { this.words = {}; }
      this.slides = Array.from(this.querySelectorAll('[data-cc-slide]'));
      this.thumbnails = Array.from(this.querySelectorAll('[data-cc-thumbnail]'));
      this.querySelector('.cc-thumbnails')?.style.setProperty('--cc-gallery-count', Math.max(1, this.slides.length));
      this.currentSlide = Math.min(this.currentSlide || 0, Math.max(0, this.slides.length - 1));
      const matchingSlide = previousBlock ? this.slides.findIndex(slide => slide.dataset.galleryBlock === previousBlock) : -1;
      if (matchingSlide >= 0) this.currentSlide = matchingSlide;
      this.currentVariant = this.variants.find(v => String(v.id) === String(previousId)) || this.variants.find(v => String(v.id) === this.variantSelect?.value) || this.variants[0];
      if (this.form && this.currentVariant) {
        this.classList.add('cc-ready');
        this.selectVariant(this.currentVariant.id, showVariant && !this.editorGalleryBlock, false);
        if (!showVariant || this.editorGalleryBlock) this.showSlide(this.currentSlide, true);
      } else this.classList.remove('cc-ready');
      this.querySelectorAll('[data-cc-tabs]').forEach(group => {
        const key = group.dataset.tabGroup;
        const desired = this.tabSelections.get(key);
        const tabs = Array.from(group.querySelectorAll('[data-cc-tab]'));
        const selected = tabs.find(tab => tab.dataset.ccTab === desired) || tabs.find(tab => tab.getAttribute('aria-selected') === 'true') || tabs[0];
        if (selected) this.activateTab(group, selected.dataset.ccTab, false, false);
      });
      this.querySelectorAll('.cc-fit').forEach(section => this.setUnit(section, this.unit || 'cm'));
      this.observeSectionImages();
      this.observer?.disconnect();
      const row = this.querySelector('.cc-purchase-row');
      const sticky = this.querySelector('[data-cc-sticky]');
      this.updateSticky();
      if (row && sticky && !sticky.hidden) {
        this.observer = new IntersectionObserver(() => this.updateSticky(), { threshold: 0 });
        this.observer.observe(row);
      }
    }
    scheduleStickyUpdate(resizeImages = false) {
      this.resizeSectionImages ||= resizeImages;
      if (this.stickyFrame != null) return;
      this.stickyFrame = requestAnimationFrame(() => {
        this.stickyFrame = null;
        this.updateSticky();
        if (this.resizeSectionImages) {
          this.resizeSectionImages = false;
          this.querySelectorAll('[data-cc-section-sized]').forEach(image => this.prepareSectionImage(image));
        }
      });
    }
    observeSectionImages() {
      this.sectionImageObserver?.disconnect();
      if (!('IntersectionObserver' in window)) return;
      this.sectionImageObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting || entry.target.closest('[hidden]')) return;
          this.prepareSectionImage(entry.target);
          this.sectionImageObserver.unobserve(entry.target);
        });
      }, { rootMargin: '900px 0px', threshold: 0 });
      this.querySelectorAll('[data-cc-section-image="true"]').forEach(image => {
        if (!image.closest('[hidden]')) this.sectionImageObserver.observe(image);
      });
    }
    prepareSectionImage(image, selected = false) {
      if (!image || image.closest('[hidden]')) return;
      const bounds = image.getBoundingClientRect();
      if (bounds.width) {
        const ratio = Number(image.getAttribute('width')) / Number(image.getAttribute('height'));
        const paintedWidth = Number.isFinite(ratio) && ratio > 0 && bounds.height ? Math.min(bounds.width, bounds.height * ratio) : bounds.width;
        image.sizes = `${Math.ceil(paintedWidth)}px`;
        image.dataset.ccSectionSized = 'true';
      }
      if (image.complete && image.naturalWidth) return;
      if (!image.dataset.ccPreviewPrepared && !navigator.connection?.saveData && !['slow-2g', '2g'].includes(navigator.connection?.effectiveType)) {
        const source = image.getAttribute('src');
        if (source) {
          const preview = new URL(source, location.href);
          preview.searchParams.set('width', '160');
          image.style.backgroundImage = `url("${preview.href}")`;
          image.dataset.ccPreviewPrepared = 'true';
          image.addEventListener('load', () => { if (image.naturalWidth) image.style.backgroundImage = ''; }, { once: true, signal: this.controller.signal });
        }
      }
      image.fetchPriority = selected ? 'high' : 'low';
      image.loading = 'eager';
    }
    updateSticky() {
      const sticky = this.querySelector('[data-cc-sticky]');
      if (!sticky) return;
      const row = this.querySelector('.cc-purchase-row');
      sticky.dataset.visible = String(Boolean(row && !sticky.hidden && row.getBoundingClientRect().bottom <= 0));
    }
    motion() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'; }
    onClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const button = target.closest('button,a[data-cc-jump]');
      if (button && this.contains(button) && !button.disabled) {
        if (button.matches('[data-cc-variant]')) this.selectVariant(button.dataset.ccVariant, true);
        else if (button.matches('[data-cc-thumbnail]')) this.showSlide(Number(button.dataset.ccThumbnail));
        else if (button.matches('[data-cc-image-retry]')) this.retryGalleryImage();
        else if (button.matches('[data-cc-step]')) this.showSlide(this.currentSlide + Number(button.dataset.ccStep));
        else if (button.matches('.cc-gallery-slide')) {
          if (!(this.suppressZoomUntil > performance.now())) this.openZoom(button);
        } else if (button.matches('[data-cc-close]')) button.closest('dialog')?.close();
        else if (button.matches('[data-cc-quantity]') && this.quantity) {
          this.quantity.value = (Number(this.quantity.value) || 1) + Number(button.dataset.ccQuantity);
          this.clampQuantity();
        } else if (button.matches('[data-cc-jump]')) {
          if (button.tagName === 'A' && button.getAttribute('href') !== '#CloseCarryProduct') return;
          event.preventDefault(); this.focusBuybox();
        }
        else if (button.matches('[data-cc-tab]')) this.activateTab(button.closest('[data-cc-tabs]'), button.dataset.ccTab, false, true);
        else if (button.matches('[data-cc-unit]')) this.setUnit(button.closest('.cc-fit'), button.dataset.ccUnit);
        else if (button.matches('[data-cc-check-fit]')) this.checkWaist(button.closest('.cc-fit'));
      }
      if (target.matches('dialog')) {
        const rect = target.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) target.close();
      }
    }
    onKeydown(event) {
      if (event.target.matches('[data-cc-waist]') && event.key === 'Enter') {
        event.preventDefault();
        this.checkWaist(event.target.closest('.cc-fit'));
        return;
      }
      const tab = event.target.closest('[data-cc-tab]');
      if (tab && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        const group = tab.closest('[data-cc-tabs]');
        const tabs = Array.from(group.querySelectorAll('[data-cc-tab]'));
        const delta = ['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1;
        let index = (tabs.indexOf(tab) + delta + tabs.length) % tabs.length;
        if (event.key === 'Home') index = 0;
        if (event.key === 'End') index = tabs.length - 1;
        this.activateTab(group, tabs[index].dataset.ccTab, true, true);
      } else if (event.target.closest('.cc-gallery-stage') && ['ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault();
        this.showSlide(this.currentSlide + (event.key === 'ArrowRight' ? 1 : -1));
      }
    }
    activateTab(group, key, focus, loadImage) {
      if (!group) return;
      const tabs = Array.from(group.querySelectorAll('[data-cc-tab]'));
      const selected = tabs.find(tab => tab.dataset.ccTab === key);
      if (!selected) return;
      this.tabSelections.set(group.dataset.tabGroup, key);
      tabs.forEach(tab => {
        const active = tab === selected;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      group.querySelectorAll('[data-cc-panel],[data-cc-explorer-image]').forEach(panel => {
        const active = (panel.dataset.ccPanel || panel.dataset.ccExplorerImage) === key;
        panel.hidden = !active;
        panel.classList.remove('cc-tab-enter');
        if (active && loadImage) {
          const image = panel.querySelector('img');
          if (image) {
            this.prepareSectionImage(image, true);
          }
          if (panel.matches('[data-cc-explorer-image],.cc-carry-panel')) panel.classList.add('cc-tab-enter');
        }
      });
      if (focus) selected.focus({ preventScroll: true });
    }
    setUnit(section, unit) {
      if (!section || !['cm', 'in'].includes(unit)) return;
      const previousUnit = section.dataset.unit || 'cm';
      const waist = section.querySelector('[data-cc-waist]');
      if (waist?.value && previousUnit !== unit) {
        const value = Number(waist.value);
        if (Number.isFinite(value) && value > 0) {
          const centimeters = waist.dataset.centimeters ? Number(waist.dataset.centimeters) : previousUnit === 'cm' ? value : value * 2.54;
          waist.dataset.centimeters = String(centimeters);
          waist.value = (unit === 'cm' ? centimeters : centimeters / 2.54).toFixed(1);
        }
      }
      this.unit = unit;
      section.dataset.unit = unit;
      section.querySelectorAll('[data-cc-unit]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.ccUnit === unit)));
      section.querySelectorAll('[data-unit-cm]').forEach(node => { node.textContent = unit === 'cm' ? node.dataset.unitCm : node.dataset.unitIn; });
      section.querySelectorAll('[data-cc-input-unit]').forEach(node => { node.textContent = unit; });
      const result = section.querySelector('[data-cc-fit-result]');
      if (result && !result.hidden) this.checkWaist(section);
    }
    checkWaist(section) {
      const check = section?.querySelector('.cc-fit-check');
      const input = check?.querySelector('[data-cc-waist]');
      const result = check?.querySelector('[data-cc-fit-result]');
      if (!check || !input || !result) return;
      const value = Number(input.value);
      const centimeters = input.dataset.centimeters ? Number(input.dataset.centimeters) : section.dataset.unit === 'in' ? value * 2.54 : value;
      const invalid = !input.value || !Number.isFinite(value) || value <= 0;
      const state = invalid ? 'invalid' : centimeters <= Number(check.dataset.maxWaist || 120) ? 'within' : 'above';
      result.textContent = check.dataset[state];
      result.dataset.state = state;
      result.hidden = false;
      input.setAttribute('aria-invalid', String(invalid));
    }
    focusBuybox() {
      const heading = this.querySelector('.cc-color-heading') || this.form;
      heading?.scrollIntoView({ behavior: this.motion(), block: 'center' });
      const button = this.querySelector('[data-cc-variant][aria-pressed="true"]') || this.form?.querySelector('[type="submit"]');
      button?.focus({ preventScroll: true });
    }
    showSlide(index, keepEditor = false) {
      if (!this.slides?.length) return;
      const previous = this.galleryPreview();
      const outgoing = this.slides.find(slide => !slide.hidden) || this.querySelector('.cc-variant-slide:not([hidden])');
      if (outgoing?.contains(document.activeElement)) this.querySelector('.cc-gallery-stage')?.focus({ preventScroll: true });
      if (!keepEditor) this.editorGalleryBlock = null;
      this.showingVariant = false;
      this.currentSlide = (index + this.slides.length) % this.slides.length;
      const variantSlide = this.querySelector('.cc-variant-slide');
      if (variantSlide) variantSlide.hidden = true;
      this.slides.forEach((slide, i) => { slide.hidden = i !== this.currentSlide; });
      this.thumbnails.forEach((button, i) => button.setAttribute('aria-pressed', String(i === this.currentSlide)));
      const slide = this.slides[this.currentSlide];
      const image = slide.querySelector('img');
      if (image) this.presentGalleryImage(slide, image, this.thumbnails[this.currentSlide]?.querySelector('img'), previous);
      this.setText('[data-cc-counter]', `${this.currentSlide + 1} / ${this.slides.length}`);
      if (slide.dataset.caption) this.setText('[data-cc-caption]', slide.dataset.caption);
    }
    warmSlide(index) {
      if (navigator.connection?.saveData || ['slow-2g', '2g'].includes(navigator.connection?.effectiveType)) return;
      const slide = this.slides?.[index];
      const image = slide?.querySelector('img');
      if (!slide?.hidden || !image || image.loading === 'eager') return;
      image.sizes = this.gallerySizes;
      image.fetchPriority = 'low';
      image.loading = 'eager';
    }
    warmNext(image) {
      const slide = image.closest('.cc-gallery-slide');
      if (!slide || slide.hidden || !image.complete || !image.naturalWidth || !this.slides?.length) return;
      this.warmVariantPreviews();
      this.warmSlide(this.showingVariant ? 0 : (this.currentSlide + 1) % this.slides.length);
    }
    warmVariantPreviews() {
      if (navigator.connection?.saveData || ['slow-2g', '2g'].includes(navigator.connection?.effectiveType)) return;
      Array.from(this.querySelectorAll('[data-cc-variant-preview]')).slice(0, 3).forEach(image => {
        image.fetchPriority = 'low';
        image.loading = 'eager';
      });
    }
    galleryPreview() {
      const overlay = this.querySelector('[data-cc-gallery-preview]');
      if (overlay && !overlay.hidden && overlay.complete && overlay.naturalWidth) return overlay.currentSrc || overlay.src;
      const image = this.querySelector('.cc-gallery-slide:not([hidden]) img');
      return image?.complete && image.naturalWidth ? image.currentSrc || image.src : '';
    }
    cancelGalleryImage() {
      this.galleryRequest = (this.galleryRequest || 0) + 1;
      this.galleryState?.controller.abort();
      clearTimeout(this.galleryState?.timer);
      this.galleryState = null;
    }
    presentGalleryImage(slide, image, thumbnail, previous = '', retry = false) {
      this.cancelGalleryImage();
      const stage = this.querySelector('.cc-gallery-stage');
      const overlay = this.querySelector('[data-cc-gallery-preview]');
      const status = this.querySelector('[data-cc-image-status]');
      const retryButton = this.querySelector('[data-cc-image-retry]');
      if (!stage || !overlay || !status || !retryButton) return;
      const state = { request: this.galleryRequest, slide, image, thumbnail, previous, controller: new AbortController() };
      this.galleryState = state;
      const options = { signal: state.controller.signal };
      const showPreview = () => {
        if (this.galleryState !== state) return;
        const matching = thumbnail?.complete && thumbnail.naturalWidth;
        const source = matching ? thumbnail.currentSrc || thumbnail.src : previous;
        state.previewPrevious = Boolean(source && !matching);
        if (source) { overlay.src = source; overlay.hidden = false; } else overlay.hidden = true;
        if (stage.dataset.imageState === 'loading') this.setText('[data-cc-image-message]', source && !matching ? this.words.imageLoadingPrevious : this.words.imageLoading);
        if (stage.dataset.imageState === 'error') this.setText('[data-cc-image-message]', state.previewPrevious ? this.words.imageErrorPrevious : this.words.imageError);
      };
      stage.dataset.imageState = 'loading';
      stage.setAttribute('aria-busy', 'true');
      status.hidden = false;
      retryButton.hidden = true;
      showPreview();
      image.addEventListener('load', () => this.finishGalleryImage(state), options);
      image.addEventListener('error', () => this.finishGalleryImage(state, true), options);
      thumbnail?.addEventListener('load', showPreview, options);
      image.sizes = this.gallerySizes;
      image.fetchPriority = 'high';
      image.loading = 'eager';
      if (thumbnail && !thumbnail.complete) { thumbnail.fetchPriority = 'high'; thumbnail.loading = 'eager'; }
      state.timer = setTimeout(() => this.finishGalleryImage(state, true), 20000);
      if (retry) {
        const src = image.getAttribute('src');
        const srcset = image.getAttribute('srcset');
        image.removeAttribute('srcset'); image.removeAttribute('src');
        requestAnimationFrame(() => {
          if (this.galleryState !== state) return;
          if (srcset) image.srcset = srcset;
          if (src) image.src = src;
        });
      } else if (image.complete) this.finishGalleryImage(state, !image.naturalWidth);
    }
    async finishGalleryImage(state, failed = false) {
      if (this.galleryState !== state || state.request !== this.galleryRequest || state.slide.hidden) return;
      if (!failed) {
        if (!state.image.complete || !state.image.naturalWidth) return;
        try { await state.image.decode?.(); } catch (_) { failed = true; }
        if (this.galleryState !== state || state.request !== this.galleryRequest || state.slide.hidden) return;
      }
      clearTimeout(state.timer);
      const stage = this.querySelector('.cc-gallery-stage');
      stage.setAttribute('aria-busy', 'false');
      if (failed) {
        stage.dataset.imageState = 'error';
        this.setText('[data-cc-image-message]', state.previewPrevious ? this.words.imageErrorPrevious : this.words.imageError);
        this.querySelector('[data-cc-image-retry]').hidden = false;
        return;
      }
      delete stage.dataset.imageState;
      this.querySelector('[data-cc-gallery-preview]').hidden = true;
      const status = this.querySelector('[data-cc-image-status]');
      if (status.contains(document.activeElement)) stage.focus({ preventScroll: true });
      status.hidden = true;
      state.controller.abort();
      this.warmNext(state.image);
    }
    retryGalleryImage() {
      const state = this.galleryState;
      if (state) this.presentGalleryImage(state.slide, state.image, state.thumbnail, state.previous, true);
    }
    openZoom(button) {
      const source = button.querySelector('img');
      const image = this.querySelector('[data-cc-lightbox-image]');
      const dialog = this.querySelector('[data-cc-lightbox]');
      if (!source || !image || !dialog) return;
      if (this.querySelector('.cc-gallery-stage')?.dataset.imageState) return;
      this.cancelZoom();
      const request = this.zoomRequest;
      const preview = source.currentSrc || source.src;
      const ratio = source.naturalWidth / source.naturalHeight || 1;
      const pixels = Math.min(innerWidth * 0.95, innerHeight * 0.93 * ratio) * (devicePixelRatio || 1);
      const maximum = Math.min(1600, Number(button.dataset.zoomWidth) || 1600);
      const width = Math.min(maximum, [400, 600, 800, 1000, 1200, 1600].find(value => value >= pixels) || 1600);
      const fullUrl = new URL(button.dataset.zoomSrc || preview, location.href);
      fullUrl.searchParams.set('width', width);
      const previewWidth = Number(new URL(preview, location.href).searchParams.get('width')) || 0;
      const full = previewWidth >= width ? preview : fullUrl.href;
      image.src = preview;
      image.alt = source.alt;
      this.zoomOpener = button;
      if (!dialog.open) dialog.showModal();
      if (!full || full === preview) return;
      const loader = new Image();
      this.zoomLoader = loader;
      loader.decoding = 'async';
      loader.fetchPriority = 'high';
      loader.onload = () => {
        if (request !== this.zoomRequest || !dialog.open || !button.isConnected) return;
        image.src = loader.currentSrc || full;
        this.zoomLoader = null;
      };
      loader.onerror = () => { if (request === this.zoomRequest) this.zoomLoader = null; };
      loader.src = full;
    }
    cancelZoom() {
      this.zoomRequest = (this.zoomRequest || 0) + 1;
      if (this.zoomLoader) {
        this.zoomLoader.onload = null; this.zoomLoader.onerror = null;
        this.zoomLoader.removeAttribute('src');
        this.zoomLoader = null;
      }
    }
    selectVariant(id, showImage, updateUrl = true) {
      const variant = this.variants?.find(v => String(v.id) === String(id));
      if (!variant || !this.variantSelect) return false;
      this.currentVariant = variant;
      this.variantSelect.value = String(variant.id);
      this.querySelectorAll('[data-cc-variant]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.ccVariant === String(variant.id))));
      this.setText('[data-cc-color]', variant.title);
      this.setText('[data-cc-price]', variant.priceText);
      this.setText('[data-cc-compare]', variant.compareText);
      this.setText('[data-cc-saving]', variant.savingText);
      this.querySelectorAll('[data-cc-compare],[data-cc-saving]').forEach(node => { node.hidden = variant.compare <= variant.price; });
      this.setText('[data-cc-stock]', variant.available ? this.words.stock : this.words.soldout);
      const stock = this.querySelector('.cc-stock');
      if (stock) stock.dataset.available = String(variant.available);
      this.setText('[data-cc-add-label]', variant.available ? this.words.add : this.words.soldout);
      this.querySelectorAll('.cc-add,[data-cc-sticky-add]').forEach(button => { button.disabled = this.busy || !variant.available; });
      const preview = this.querySelector('[data-cc-variant-image]');
      const slide = this.querySelector('.cc-variant-slide');
      if (showImage && variant.image && preview && slide) {
        const previous = this.galleryPreview();
        this.showingVariant = true;
        this.editorGalleryBlock = null;
        this.slides.forEach(node => { node.hidden = true; });
        this.thumbnails.forEach(button => button.setAttribute('aria-pressed', 'false'));
        preview.sizes = this.gallerySizes;
        preview.fetchPriority = 'high';
        preview.loading = 'eager';
        if (preview.dataset.imageVariant !== String(variant.id)) {
          if (variant.srcset) preview.srcset = variant.srcset; else preview.removeAttribute('srcset');
          preview.src = variant.image;
          preview.dataset.imageVariant = String(variant.id);
        }
        preview.alt = variant.title;
        slide.dataset.zoomSrc = variant.zoom || variant.image;
        slide.dataset.zoomSmall = variant.zoomSmall || variant.zoom || variant.image;
        slide.dataset.zoomWidth = variant.imageWidth || 1600;
        slide.hidden = false;
        this.setText('[data-cc-counter]', variant.title);
        this.presentGalleryImage(slide, preview, this.querySelector(`[data-cc-variant-preview="${variant.id}"]`), previous);
      }
      if (updateUrl) {
        const url = new URL(location.href);
        url.searchParams.set('variant', String(variant.id));
        history.replaceState({}, '', url);
      }
      return true;
    }
    setText(selector, value) { this.querySelectorAll(selector).forEach(node => { node.textContent = value || ''; }); }
    clampQuantity() { if (this.quantity) this.quantity.value = Math.max(1, Math.min(99, Math.floor(Number(this.quantity.value) || 1))); }
    async openCartDrawer(drawer, opener) {
      if (this.openingCart) return;
      this.openingCart = true;
      opener.setAttribute('aria-busy', 'true');
      try {
        const sections = drawer.getSectionsToRender().map(section => section.id);
        const response = await fetch(window.routes.cart_update_url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: {}, sections, sections_url: location.pathname })
        });
        const cart = await response.json();
        const rendered = cart.sections || {};
        if (Number.isFinite(cart.item_count)) this.setText('[data-cc-cart-count]', String(cart.item_count));
        if (!response.ok || sections.some(id => !rendered[id])) throw new Error('Cart rendering unavailable');
        drawer.setActiveElement(opener);
        const parsed = new DOMParser().parseFromString(rendered['cart-drawer'], 'text/html');
        drawer.classList.toggle('is-empty', parsed.querySelector('cart-drawer')?.classList.contains('is-empty') ?? false);
        drawer.renderContents({ sections: rendered });
        this.refreshCartCount();
      } catch (failure) {
        console.warn('Unable to refresh the cart drawer', failure);
        window.location.assign(this.dataset.cartUrl);
      } finally {
        this.openingCart = false;
        opener.removeAttribute('aria-busy');
      }
    }
    refreshCartCount() {
      fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } })
        .then(response => response.json()).then(cart => {
          if (Number.isFinite(cart.item_count)) this.setText('[data-cc-cart-count]', String(cart.item_count));
        }).catch(() => {});
    }
    async addToCart(event) {
      event.preventDefault();
      if (!this.form || this.busy || !this.currentVariant?.available || !this.form.reportValidity()) return;
      this.busy = true;
      this.clampQuantity();
      const variant = this.currentVariant;
      const quantity = Number(this.quantity?.value) || 1;
      this.cartOpener = event.submitter || this.form.querySelector('[type="submit"]');
      const error = this.querySelector('[data-cc-error]');
      if (error) error.hidden = true;
      this.querySelectorAll('.cc-add,[data-cc-sticky-add]').forEach(button => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
      this.setText('[data-cc-add-label]', this.words.adding);
      try {
        const drawer = document.querySelector('cart-drawer');
        const sections = typeof drawer?.getSectionsToRender === 'function' ? drawer.getSectionsToRender().map(section => section.id) : [];
        drawer?.setActiveElement?.(this.cartOpener);
        const response = await fetch(`${this.dataset.cartAddUrl}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: variant.id, quantity }], sections, sections_url: location.pathname }) });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || this.words.error);
        // A successful add must never be retried if drawer rendering fails.
        try {
          if (!sections.length || !result.sections || sections.some(id => !result.sections[id])) throw new Error('Cart rendering unavailable');
          drawer.classList.remove('is-empty');
          // Keep the membership popup first; its X opens this refreshed drawer.
          const popupOpen = !!document.querySelector('.vf-root.open');
          drawer.renderContents(result, popupOpen);
          this.refreshCartCount();
        } catch {
          window.location.assign(this.dataset.cartUrl);
        }
      } catch (failure) {
        if (error) { error.textContent = failure.message || this.words.error; error.hidden = false; error.scrollIntoView({ behavior: this.motion(), block: 'center' }); }
      } finally {
        this.busy = false;
        this.querySelectorAll('.cc-add,[data-cc-sticky-add]').forEach(button => { button.disabled = !this.currentVariant?.available; button.removeAttribute('aria-busy'); });
        this.setText('[data-cc-add-label]', this.currentVariant?.available ? this.words.add : this.words.soldout);
      }
    }
  }
  customElements.define('closecarry-page', CloseCarryPage);
}
